import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Trash2 } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { StatusBadge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Input';
import { useJobs, useJobActions, type JobsQuery } from '@/hooks/useDispatcher';
import { formatDateTime, formatDuration } from '@/lib/utils';
import type { Job } from '@/types/api';

export default function JobsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<JobsQuery>({});
  const { data, isLoading } = useJobs(filters);
  const { remove } = useJobActions();

  function handleRemove(job: Job, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm(`Supprimer le job ${job.source_job_id ?? job.id.slice(0, 8)} ?`)) return;
    remove.mutate(job.id, {
      onSuccess: () => toast.success('Job supprimé'),
      onError: () => toast.error('Échec de la suppression'),
    });
  }

  const columns = useMemo<ColumnDef<Job>[]>(
    () => [
      { accessorKey: 'source', header: 'Source' },
      {
        accessorKey: 'source_job_id',
        header: 'Job ID',
        cell: ({ getValue }) => <span className="font-mono text-xs">{getValue<string>()}</span>,
      },
      { accessorKey: 'industry', header: 'Industrie' },
      { accessorKey: 'location_query', header: 'Localisation' },
      { accessorKey: 'size', header: 'Taille', cell: ({ getValue }) => getValue<number>() ?? '—' },
      {
        accessorKey: 'status',
        header: 'Statut',
        cell: ({ getValue }) => <StatusBadge status={getValue<string>()} />,
      },
      { accessorKey: 'vm_slug', header: 'VM', cell: ({ getValue }) => getValue<string>() ?? '—' },
      {
        accessorKey: 'eta_ms',
        header: 'ETA',
        cell: ({ getValue }) => formatDuration(getValue<number | null>()),
      },
      {
        accessorKey: 'started_at',
        header: 'Démarré',
        cell: ({ getValue }) => formatDateTime(getValue<string | null>()),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => handleRemove(row.original, e)}
            className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
            title="Supprimer le job"
          >
            <Trash2 size={12} />
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader title="Jobs" description="Tous les jobs LeadScrape dispatchés" />

      <DataTable
        data={data}
        columns={columns}
        loading={isLoading}
        searchPlaceholder="Filtrer par industrie, ville, source…"
        onRowClick={(row) => navigate(`/jobs/${row.id}`)}
        emptyTitle="Aucun job"
        emptyDescription="Les jobs apparaîtront ici dès qu'un batch est créé."
        rightSlot={
          <div className="flex items-center gap-2">
            <Select
              value={filters.status ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
              className="w-32"
            >
              <option value="">Tous statuts</option>
              <option value="queued">queued</option>
              <option value="running">running</option>
              <option value="done">done</option>
              <option value="failed">failed</option>
              <option value="cached">cached</option>
            </Select>
            <Select
              value={filters.source ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value || undefined }))}
              className="w-36"
            >
              <option value="">Toutes sources</option>
              <option value="getfishnet">getfishnet</option>
              <option value="bigcall">bigcall</option>
            </Select>
          </div>
        }
      />
    </div>
  );
}
